/**
 * DeviceCapabilityGate — pre-flight ADVISORY + DEGRADATION HINTS for tools.
 *
 * Philosophy (revised 2026-05-08): the gate's job is not to block users — it's
 * to help tools degrade gracefully so EVERY user gets a result, not an error.
 * Two kinds of hints surfaced:
 *
 *   1. SIMD-unsupported CPUs (pre-2008 hardware) — FFmpeg WASM can't init.
 *      The gate shows a non-blocking advisory ABOVE the uploader: zone stays
 *      interactive, user can still try. If FFmpeg actually fails, the
 *      existing localised error message kicks in. (Future: WebAudio-native
 *      fallback path will let SIMD-less devices process via JS-only — gate
 *      will then disappear when that fallback is registered.)
 *
 *   2. Memory-marginal devices on too-large files — FFmpeg can crash mid-
 *      process with `Aborted()`. Gate emits `devgate:memory-marginal` event
 *      with a `suggestedDownsample` payload (e.g. 22 kHz mono). Tools listen
 *      and AUTO-DOWNSAMPLE before the main pipeline — no warning, no error,
 *      file gets processed at slightly lower quality. Inline notice still
 *      shown (transparency: "we reduced this to fit your RAM"), but it's
 *      informational, not actionable.
 *
 * Usage shape (set via `<x-tool-uploader gate="ffmpeg+memory" toolSlug="...">`):
 *
 *   gate.installAdvisory(uploaderEl, { slug, locale });          // SIMD branch
 *   gate.installMemoryWatcher(uploaderEl, { slug, locale });     // memory branch
 *   uploaderEl.addEventListener('devgate:memory-marginal', e => {
 *       // e.detail = { file, predictedMB, deviceGB, suggestedDownsample: {ar, ac} }
 *       // tool's runEnhance() pre-converts file via FFmpeg with these params
 *   });
 *
 * All installers idempotent — calling twice is a no-op.
 *
 * Telemetry surface:
 *   feature_use { action: 'simd_advisory_shown', slug }
 *   feature_use { action: 'memory_marginal', slug, fileMB, deviceGB }
 *   feature_use { action: 'memory_blocked',   slug, fileMB, deviceGB }
 *
 * No external deps — uses only window.__hasWasmSIMD (inline-bootstrap),
 * window.__LOCALE, window.__ae for telemetry, navigator.deviceMemory.
 */
(function() {
    'use strict';
    if (window.DeviceCapabilityGate) return; // already loaded (defer race-safe)

    // Localised banner copy. 8 locales — kept inline because the gate runs
    // BEFORE the tool's translation bundle is necessarily ready (defer order
    // independence). Keys mirror tools/audio-enhancer.simd_unsupported keys
    // for consistency, but the lookup is fully self-contained.
    var L_SIMD = {
        en: {
            title: 'Processing not supported on this device',
            body: "This device's CPU doesn't support modern WebAssembly (SIMD), which the processing tools here require. This usually means hardware from before ~2010. A browser update won't help — try a newer computer or phone.",
            cta: 'Learn more'
        },
        ru: {
            title: 'Обработка недоступна на этом устройстве',
            body: 'Процессор этого устройства не поддерживает современный WebAssembly (SIMD), который нужен инструментам обработки. Обычно это техника до ~2010 года. Обновление браузера не поможет — попробуйте более новый компьютер или телефон.',
            cta: 'Подробнее'
        },
        ko: {
            title: '이 기기에서는 처리를 사용할 수 없습니다',
            body: '이 기기의 CPU는 처리 도구에 필요한 최신 WebAssembly(SIMD)를 지원하지 않습니다. 보통 2010년 이전 하드웨어입니다. 브라우저 업데이트로는 해결되지 않습니다 — 더 최신 컴퓨터나 휴대폰을 사용해 보세요.',
            cta: '자세히'
        },
        ar: {
            title: 'المعالجة غير متاحة على هذا الجهاز',
            body: 'معالج هذا الجهاز لا يدعم WebAssembly الحديث (SIMD) الذي تحتاجه أدوات المعالجة. عادةً أجهزة أقدم من ~2010. تحديث المتصفح لن يساعد — جرّب حاسوبًا أو هاتفًا أحدث.',
            cta: 'المزيد'
        },
        id: {
            title: 'Pemrosesan tidak tersedia di perangkat ini',
            body: 'CPU perangkat ini tidak mendukung WebAssembly modern (SIMD) yang dibutuhkan alat pemrosesan. Biasanya perangkat sebelum ~2010. Memperbarui browser tidak akan membantu — coba komputer atau ponsel yang lebih baru.',
            cta: 'Selengkapnya'
        },
        pt: {
            title: 'Processamento não disponível neste dispositivo',
            body: 'O processador deste dispositivo não suporta WebAssembly moderno (SIMD), necessário para as ferramentas de processamento. Geralmente hardware anterior a ~2010. Atualizar o navegador não ajuda — tente um computador ou celular mais novo.',
            cta: 'Saiba mais'
        },
        de: {
            title: 'Verarbeitung auf diesem Gerät nicht verfügbar',
            body: 'Der Prozessor dieses Geräts unterstützt kein modernes WebAssembly (SIMD), das die Verarbeitungs-Tools benötigen. Meist Hardware vor ~2010. Ein Browser-Update hilft nicht — bitte ein neueres Gerät verwenden.',
            cta: 'Mehr erfahren'
        },
        es: {
            title: 'Procesamiento no disponible en este dispositivo',
            body: 'El procesador de este dispositivo no admite WebAssembly moderno (SIMD), necesario para las herramientas de procesamiento. Normalmente hardware anterior a ~2010. Actualizar el navegador no ayuda — prueba un equipo o móvil más nuevo.',
            cta: 'Más información'
        },
        fr: {
            title: 'Traitement non disponible sur cet appareil',
            body: "Le processeur de cet appareil ne prend pas en charge WebAssembly moderne (SIMD), requis par les outils de traitement. Généralement du matériel antérieur à ~2010. Mettre à jour le navigateur n'y changera rien — essayez un appareil plus récent.",
            cta: 'En savoir plus'
        },
        uk: {
            title: 'Обробка недоступна на цьому пристрої',
            body: 'Процесор цього пристрою не підтримує сучасний WebAssembly (SIMD), потрібний інструментам обробки. Зазвичай це техніка до ~2010 року. Оновлення браузера не допоможе — спробуйте новіший комп’ютер або телефон.',
            cta: 'Докладніше'
        },
        tr: {
            title: 'Bu cihazda işleme desteklenmiyor',
            body: 'Bu cihazın işlemcisi, buradaki işleme araçlarının gerektirdiği modern WebAssembly (SIMD) desteğine sahip değil. Bu genellikle ~2010 öncesi donanım anlamına gelir. Tarayıcı güncellemesi işe yaramaz — daha yeni bir bilgisayar veya telefon deneyin.',
            cta: 'Daha fazla bilgi'
        }
    };

    var L_MEM = {
        en: { note: function(mb, gb){ return 'Heads up: file is ' + mb + ' MB and your device has ' + gb + ' GB RAM. We will automatically reduce the input to 22 kHz mono before processing so it fits in memory. Output will be slightly lower fidelity than the original.'; } },
        ru: { note: function(mb, gb){ return 'Внимание: файл ' + mb + ' МБ, а у устройства ' + gb + ' ГБ ОЗУ. Перед обработкой автоматически снизим вход до 22 кГц моно, чтобы влезло в память. Качество результата будет чуть ниже исходника.'; } },
        ko: { note: function(mb, gb){ return '안내: 파일 크기는 ' + mb + ' MB이고 기기 RAM은 ' + gb + ' GB입니다. 메모리에 맞추기 위해 처리 전 22 kHz 모노로 자동 다운샘플링합니다. 결과 품질은 원본보다 약간 낮아집니다.'; } },
        ar: { note: function(mb, gb){ return 'تنبيه: حجم الملف ' + mb + ' ميجابايت وذاكرة جهازك ' + gb + ' جيجابايت. سنقوم بتقليل المدخل إلى 22 كيلوهرتز أحادي قبل المعالجة لتلائم الذاكرة. ستكون جودة الناتج أقل قليلاً من الأصل.'; } },
        id: { note: function(mb, gb){ return 'Perhatian: file ' + mb + ' MB dan RAM perangkat ' + gb + ' GB. Kami akan otomatis menurunkan input ke 22 kHz mono sebelum diproses agar muat di memori. Kualitas hasil sedikit lebih rendah dari asli.'; } },
        pt: { note: function(mb, gb){ return 'Aviso: o arquivo tem ' + mb + ' MB e seu dispositivo tem ' + gb + ' GB de RAM. Reduziremos automaticamente a entrada para 22 kHz mono antes do processamento para caber na memória. A qualidade ficará ligeiramente abaixo do original.'; } },
        de: { note: function(mb, gb){ return 'Hinweis: Die Datei hat ' + mb + ' MB und Ihr Gerät verfügt über ' + gb + ' GB RAM. Wir reduzieren die Eingabe vor der Verarbeitung automatisch auf 22 kHz Mono, damit es in den Speicher passt. Die Ausgabequalität liegt leicht unter dem Original.'; } },
        es: { note: function(mb, gb){ return 'Aviso: el archivo es de ' + mb + ' MB y tu dispositivo tiene ' + gb + ' GB de RAM. Reduciremos automáticamente la entrada a 22 kHz mono antes de procesar para que quepa en memoria. La calidad será ligeramente inferior al original.'; } },
        fr: { note: function(mb, gb){ return "Note : le fichier fait " + mb + " Mo et votre appareil a " + gb + " Go de RAM. Nous réduirons automatiquement l'entrée à 22 kHz mono avant le traitement pour tenir en mémoire. La qualité sera légèrement inférieure à l'original."; } },
        uk: { note: function(mb, gb){ return 'Увага: файл ' + mb + ' МБ, а у пристрою ' + gb + ' ГБ ОЗП. Перед обробкою автоматично знизимо вхід до 22 кГц моно, щоб він умістився в пам’ять. Якість результату буде трохи нижчою за оригінал.'; } },
        tr: { note: function(mb, gb){ return 'Bilgi: dosya ' + mb + ' MB ve cihazınızda ' + gb + ' GB RAM var. Belleğe sığması için işlemeden önce girişi otomatik olarak 22 kHz mono biçimine düşüreceğiz. Çıktının kalitesi orijinalden biraz daha düşük olacaktır.'; } }
    };

    // "warn" mode — for tools that can't downsample (lossless trim, video
    // processing). Tells the user processing may run out of memory so they
    // can pick a smaller file or use a desktop before hitting an opaque
    // OOM mid-pipeline. Used via data-gate-mode="warn" on the uploader.
    var L_MEM_WARN = {
        en: { note: function(mb, gb){ return 'Heads up: file is ' + mb + ' MB and your device has ' + gb + ' GB RAM. Processing may fail with an out-of-memory error. Try a smaller file or run this on a desktop with more RAM.'; } },
        ru: { note: function(mb, gb){ return 'Внимание: файл ' + mb + ' МБ, а у устройства ' + gb + ' ГБ ОЗУ. Обработка может прерваться нехваткой памяти. Попробуйте файл поменьше или запустите инструмент на компьютере с большим объёмом ОЗУ.'; } },
        ko: { note: function(mb, gb){ return '안내: 파일 크기는 ' + mb + ' MB이고 기기 RAM은 ' + gb + ' GB입니다. 메모리 부족으로 처리가 실패할 수 있습니다. 더 작은 파일을 사용하거나 RAM이 더 큰 데스크톱에서 실행해 보세요.'; } },
        ar: { note: function(mb, gb){ return 'تنبيه: حجم الملف ' + mb + ' ميجابايت وذاكرة جهازك ' + gb + ' جيجابايت. قد تفشل المعالجة بسبب نفاد الذاكرة. جرّب ملفاً أصغر أو شغّل الأداة على حاسوب مكتبي بذاكرة أكبر.'; } },
        id: { note: function(mb, gb){ return 'Perhatian: file ' + mb + ' MB dan RAM perangkat ' + gb + ' GB. Pemrosesan bisa gagal karena memori tidak cukup. Coba file yang lebih kecil atau gunakan desktop dengan RAM lebih besar.'; } },
        pt: { note: function(mb, gb){ return 'Aviso: o arquivo tem ' + mb + ' MB e seu dispositivo tem ' + gb + ' GB de RAM. O processamento pode falhar por falta de memória. Use um arquivo menor ou rode em um desktop com mais RAM.'; } },
        de: { note: function(mb, gb){ return 'Hinweis: Die Datei hat ' + mb + ' MB und Ihr Gerät verfügt über ' + gb + ' GB RAM. Die Verarbeitung könnte aufgrund unzureichenden Speichers fehlschlagen. Versuchen Sie eine kleinere Datei oder einen Desktop mit mehr RAM.'; } },
        es: { note: function(mb, gb){ return 'Aviso: el archivo es de ' + mb + ' MB y tu dispositivo tiene ' + gb + ' GB de RAM. El procesamiento podría fallar por falta de memoria. Prueba un archivo más pequeño o usa un equipo de escritorio con más RAM.'; } },
        fr: { note: function(mb, gb){ return "Note : le fichier fait " + mb + " Mo et votre appareil a " + gb + " Go de RAM. Le traitement risque d'échouer par manque de mémoire. Essayez un fichier plus petit ou un poste de bureau avec plus de RAM."; } },
        uk: { note: function(mb, gb){ return 'Увага: файл ' + mb + ' МБ, а у пристрою ' + gb + ' ГБ ОЗП. Обробка може перерватися через нестачу пам’яті. Спробуйте менший файл або запустіть інструмент на комп’ютері з більшим обсягом ОЗП.'; } },
        tr: { note: function(mb, gb){ return 'Bilgi: dosya ' + mb + ' MB ve cihazınızda ' + gb + ' GB RAM var. İşlem bellek yetersizliğinden başarısız olabilir. Daha küçük bir dosya deneyin veya daha yüksek belleğe sahip bir bilgisayar kullanın.'; } }
    };

    // Heuristic for OOM probability. Decoded audio at 48 kHz stereo float32 ~= 384 KB/s,
    // 30 min file → ~700 MB decoded. FFmpeg keeps full input + decoded + output buffers,
    // realistic peak is ~3× the input file size. Browsers cap WASM heap at 4 GB hard,
    // and per-tab JS allocation is roughly half of deviceMemory in practice (other tabs,
    // browser overhead). The 0.5 multiplier is conservative — we'd rather warn slightly
    // too often than let users hit Aborted() with no warning.
    function predictPeakMB(fileBytes) {
        return Math.ceil(fileBytes / 1048576) * 3;
    }

    /* WebAssembly that does not RUN — absent (Lockdown mode, old engines) or
       present but forbidden to compile (in-app browsers: «Wasm code generation
       disallowed by embedder»; validate() and the SIMD probe still answer true
       there, because they parse without generating code). Saying «your CPU is
       too old» to these people is false, and no ORT build can help them: the
       one useful thing is a regular browser. Same probe as the FFmpeg door
       (ffmpeg-processor.js _wasmCompiles). Added 2026-10-10 with the ORT 1.18
       lane for CPUs without SIMD (window.__ortBuild.legacy()). */
    var L_WASM = {
        en: { title: 'This browser does not run WebAssembly', body: 'This tool needs WebAssembly, and this browser does not run it — typical of in-app browsers and hardened security modes. Open the page in a regular browser: Chrome, Firefox, Edge or Safari.' },
        ru: { title: 'Этот браузер не запускает WebAssembly', body: 'Инструменту нужен WebAssembly, а этот браузер его не запускает — так бывает во встроенных браузерах приложений и в режимах повышенной защиты. Откройте страницу в обычном браузере: Chrome, Яндекс Браузер, Firefox или Safari.' },
        ko: { title: '이 브라우저는 WebAssembly를 실행하지 않습니다', body: '이 도구에는 WebAssembly가 필요하지만 이 브라우저는 실행하지 않습니다. 앱 내 브라우저나 강화된 보안 모드에서 흔히 발생합니다. Chrome, Firefox, Edge 또는 Safari 같은 일반 브라우저에서 페이지를 열어 주세요.' },
        ar: { title: 'هذا المتصفح لا يشغّل WebAssembly', body: 'تحتاج هذه الأداة إلى WebAssembly، وهذا المتصفح لا يشغّله — يحدث هذا عادةً في المتصفحات المدمجة داخل التطبيقات وفي أوضاع الحماية المشددة. افتح الصفحة في متصفح عادي: Chrome أو Firefox أو Edge أو Safari.' },
        id: { title: 'Browser ini tidak menjalankan WebAssembly', body: 'Alat ini membutuhkan WebAssembly, dan browser ini tidak menjalankannya — biasa terjadi pada browser bawaan aplikasi dan mode keamanan ketat. Buka halaman di browser biasa: Chrome, Firefox, Edge, atau Safari.' },
        pt: { title: 'Este navegador não executa WebAssembly', body: 'Esta ferramenta precisa de WebAssembly, e este navegador não o executa — comum em navegadores dentro de apps e em modos de segurança reforçada. Abra a página em um navegador comum: Chrome, Firefox, Edge ou Safari.' },
        de: { title: 'Dieser Browser führt kein WebAssembly aus', body: 'Dieses Tool braucht WebAssembly, und dieser Browser führt es nicht aus – typisch für In-App-Browser und verschärfte Sicherheitsmodi. Öffne die Seite in einem normalen Browser: Chrome, Firefox, Edge oder Safari.' },
        es: { title: 'Este navegador no ejecuta WebAssembly', body: 'Esta herramienta necesita WebAssembly y este navegador no lo ejecuta, algo habitual en los navegadores integrados en apps y en los modos de seguridad reforzada. Abre la página en un navegador común: Chrome, Firefox, Edge o Safari.' },
        fr: { title: "Ce navigateur n'exécute pas WebAssembly", body: "Cet outil a besoin de WebAssembly, et ce navigateur ne l'exécute pas — c'est fréquent dans les navigateurs intégrés aux applications et dans les modes de sécurité renforcée. Ouvrez la page dans un navigateur classique : Chrome, Firefox, Edge ou Safari." },
        uk: { title: 'Цей браузер не запускає WebAssembly', body: 'Інструменту потрібен WebAssembly, а цей браузер його не запускає — так буває у вбудованих браузерах застосунків і в режимах посиленого захисту. Відкрийте сторінку у звичайному браузері: Chrome, Firefox, Edge або Safari.' },
        tr: { title: 'Bu tarayıcı WebAssembly çalıştırmıyor', body: 'Bu araç WebAssembly gerektirir ve bu tarayıcı onu çalıştırmıyor — uygulama içi tarayıcılarda ve güçlendirilmiş güvenlik modlarında sık görülür. Sayfayı normal bir tarayıcıda açın: Chrome, Firefox, Edge veya Safari.' }
    };

    function _locale() {
        var l = (window.__LOCALE || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
        return L_SIMD[l] ? l : 'en';
    }

    function _ae(action, payload) {
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', Object.assign({ action: action }, payload || {}));
            }
        } catch (_) {}
    }

    var _api = {
        version: '1.0.0',

        /**
         * Cached SIMD detection. Reads from window.__hasWasmSIMD when available
         * (inline-bootstrap in layouts/app.blade.php). Returns false if the
         * helper is missing — we don't want to assume support on unknown
         * environments where the bootstrap failed to run.
         */
        /** WebAssembly present AND allowed to compile here (8-byte empty module). */
        wasmCompiles: function() {
            if (typeof this._wasmRuns === 'boolean') return this._wasmRuns;
            try {
                this._wasmRuns = typeof WebAssembly === 'object'
                    && !!new WebAssembly.Module(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0]));
            } catch (_) { this._wasmRuns = false; }
            return this._wasmRuns;
        },

        hasWasmSIMD: function() {
            if (typeof this._simd === 'boolean') return this._simd;
            try {
                this._simd = (typeof window.__hasWasmSIMD === 'function') && !!window.__hasWasmSIMD();
            } catch (_) { this._simd = false; }
            return this._simd;
        },

        /** Device RAM in GB; null when navigator.deviceMemory is unsupported (Safari, Firefox). */
        deviceMemoryGB: function() {
            return (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory > 0)
                ? navigator.deviceMemory : null;
        },

        /**
         * File-level pre-flight. Returns { ok, severity, reason, predictedMB }.
         *   severity = 'ok' | 'marginal' | 'blocked'
         *   marginal = warn user but allow proceed
         *   blocked  = file × 3 > deviceMemory total (will OOM with high confidence)
         */
        preflightFile: function(file) {
            if (!file || !file.size) return { ok: true, severity: 'ok', predictedMB: 0 };
            var dmGB = this.deviceMemoryGB();
            var predictedMB = predictPeakMB(file.size);
            if (!dmGB) return { ok: true, severity: 'ok', predictedMB: predictedMB };
            // 0.5× total memory = realistic per-tab JS quota (browsers commonly limit there).
            var quotaMB = dmGB * 1024 * 0.5;
            if (predictedMB > dmGB * 1024 * 0.9) {
                return { ok: false, severity: 'blocked', predictedMB: predictedMB, deviceGB: dmGB, reason: 'predicted_oom' };
            }
            if (predictedMB > quotaMB) {
                return { ok: true, severity: 'marginal', predictedMB: predictedMB, deviceGB: dmGB, reason: 'memory_marginal' };
            }
            return { ok: true, severity: 'ok', predictedMB: predictedMB, deviceGB: dmGB };
        },

        /**
         * Show a non-blocking advisory ABOVE the uploader on SIMD-less devices.
         * Zone stays interactive — user can still try. If FFmpeg fails at
         * click-time the existing localised error message takes over.
         * Idempotent. Emits `simd_advisory_shown` once per session.
         */
        /**
         * requireSimd(slug) — programmatic HARD pre-flight for tools whose
         * main lane cannot run without WASM SIMD (every ORT-Web consumer:
         * ORT ≥1.19 ships only the SIMD binary; `ort.env.wasm.simd=false`
         * is a placebo — proven 2026-07-30, simdFallback recovered 0 users
         * in all telemetry history). Call it BEFORE fetching model bytes so
         * an incapable CPU never downloads 30–270 MB for a guaranteed
         * compile error (req deep667: ~8 wasted minutes).
         *
         *   var g = window.DeviceCapabilityGate.requireSimd('photo-colorize');
         *   if (!g.ok) { showError(g.message); return; }
         *
         * Returns { ok:true } or { ok:false, title, message } with the
         * localized honest explanation (CPU age, browser update won't help)
         * from the same dictionary the banner uses — one source of truth,
         * no per-tool lang keys. Emits typed telemetry once per call site.
         */
        requireSimd: function(slug, opts) {
            var blocked = !this.wasmCompiles();
            if (!blocked && this.hasWasmSIMD()) return { ok: true };
            var copy = blocked ? (L_WASM[_locale()] || L_WASM.en) : (L_SIMD[_locale()] || L_SIMD.en);
            // opts.silent — text-only lookup (tooltips, pre-rendered notes):
            // no telemetry. Default (a real blocked attempt) emits the typed
            // simd_unsupported error so funnels see the wall being hit.
            if (!(opts && opts.silent)) {
                try {
                    if (typeof window.__aeToolError === 'function') {
                        window.__aeToolError({ slug: slug || 'unknown',
                            error_type: blocked ? 'wasm_blocked' : 'simd_unsupported',
                            msg: blocked ? 'preflight: wasm does not compile here' : 'preflight: wasm simd absent' });
                    } else {
                        _ae('simd_blocked', { slug: slug || 'unknown' });
                    }
                } catch (_) {}
            }
            return { ok: false, title: copy.title, message: copy.body };
        },

        installAdvisory: function(uploaderEl, opts) {
            if (!uploaderEl || uploaderEl.__deviceGateAdvisory) return;
            uploaderEl.__deviceGateAdvisory = true;
            if (this.hasWasmSIMD()) return; // capability OK — nothing to do
            var slug = (opts && opts.slug) || uploaderEl.getAttribute('data-tool-slug') || 'unknown';
            var loc = (opts && opts.locale) || _locale();
            var copy = L_SIMD[loc] || L_SIMD.en;

            uploaderEl.classList.add('device-gate-advisory');
            uploaderEl.setAttribute('data-simd', 'missing');

            // Render advisory ABOVE the uploader (not replacing). Zone stays
            // interactive, user can still pick a file and try. We're honest
            // about the risk, not blocking the attempt.
            var advisory = document.createElement('div');
            advisory.className = 'device-gate-advisory';
            advisory.setAttribute('role', 'note');
            advisory.style.cssText = 'margin:0 0 12px;padding:12px 16px;border-radius:10px;'
                + 'background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);'
                + 'color:var(--text-primary,#fff);font-size:13px;line-height:1.55;'
                + 'display:flex;gap:12px;align-items:flex-start;max-width:900px;margin-left:auto;margin-right:auto;';
            var icon = document.createElement('span');
            icon.style.cssText = 'font-size:20px;flex-shrink:0;line-height:1.2';
            icon.textContent = '⚠️';
            var msgWrap = document.createElement('div');
            var h = document.createElement('div');
            h.style.cssText = 'font-weight:600;margin-bottom:4px';
            h.textContent = copy.title;
            var p = document.createElement('div');
            p.style.cssText = 'color:var(--text-secondary,#aaa)';
            p.textContent = copy.body;
            msgWrap.appendChild(h); msgWrap.appendChild(p);
            advisory.appendChild(icon); advisory.appendChild(msgWrap);
            if (uploaderEl.parentNode) {
                uploaderEl.parentNode.insertBefore(advisory, uploaderEl);
            }

            _ae('simd_advisory_shown', { slug: slug, dm: this.deviceMemoryGB() || 0,
                br: (window.__brOf ? window.__brOf(navigator.userAgent) : 'other'),
                mob: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 1 : 0 });
        },

        /**
         * Wire memory pre-flight to the uploader's `tool-uploader:file` event.
         * On marginal/blocked file, append an inline notice below the uploader
         * (or replace the file metadata block), and emit telemetry. Returns
         * the listener handle so callers can detach if they replace uploaders.
         */
        installMemoryWatcher: function(uploaderEl, opts) {
            if (!uploaderEl || uploaderEl.__deviceGateMemoryWatcher) return;
            uploaderEl.__deviceGateMemoryWatcher = true;
            var slug = (opts && opts.slug) || uploaderEl.getAttribute('data-tool-slug') || 'unknown';
            var self = this;
            var loc = (opts && opts.locale) || _locale();

            var noticeEl = null;
            var clear = function() {
                if (noticeEl && noticeEl.parentNode) noticeEl.parentNode.removeChild(noticeEl);
                noticeEl = null;
            };

            // Race-safe binding via __bindUploaderFile when available (closes
            // file-picked-before-listener-attached race; tool-uploader.js >=
            // 2026-05-08). Falls back to raw addEventListener for defensive
            // operation if the helper isn't present yet.
            var fileHandler = function(file) {
                clear();
                if (!file) return;
                /* «file × 3» is the cost of a file that enters memory. The zone
                   stamps every accepted file with the size kind it was judged
                   by (tool-uploader.js stampAccepted), and a streaming kind
                   means the lane reads it from disk in slices: /audio-enhancer
                   keeps only a video's SOUND (48 kHz WAV via WORKERFS), so a
                   3 GB lecture on an 8 GB machine was «blocked» here and on
                   16 GB was promised a 22 kHz mono downsample that the video
                   branch never runs (01.10.2026). */
                var zk = file.__tuGate && file.__tuGate.kind;
                if (zk && /^video(Demux|WebCodecs|Stream)$/.test(zk)) return;
                var pre = self.preflightFile(file);
                if (pre.severity === 'ok') return;

                // Suggested downsample params: 22 kHz mono is the standard FFmpeg
                // OOM-mitigation form (existing `oomChoice` modal in audio-enhancer
                // uses the same `-ar 22050 -ac 1`). Memory cost goes from
                // file × 3 to roughly file × 0.5 (decoded buffer 4× smaller +
                // less FFT work). Tools listen for `devgate:memory-marginal`
                // and pre-convert via FFmpeg BEFORE the main pipeline. Result
                // is a slightly lower-quality file delivered, instead of an
                // FFmpeg `Aborted()` and no result at all.
                var suggested = { ar: 22050, ac: 1 };

                // Emit DOM event so tools can wire degradation gracefully.
                // Detail carries everything a tool needs to decide: file
                // reference, predicted memory cost, device RAM, suggested
                // downsample params, severity (marginal | blocked).
                try {
                    uploaderEl.dispatchEvent(new CustomEvent('devgate:memory-' + pre.severity, {
                        detail: {
                            file: file,
                            severity: pre.severity,
                            fileMB: Math.round(file.size / 1048576),
                            deviceGB: pre.deviceGB,
                            predictedMB: pre.predictedMB,
                            suggestedDownsample: suggested,
                            slug: slug
                        }
                    }));
                } catch (_) {}

                // Inline notice. Two modes — chosen per uploader via
                // data-gate-mode:
                //  - "downsample" (default) — informational, "we'll auto-
                //    downsample this". For tools that listen to the event
                //    and pre-convert (audio-enhancer).
                //  - "warn"               — actionable, "this may fail, try
                //    smaller file". For tools that can't downsample without
                //    altering the user-facing output (lossless trim, video).
                var mode = uploaderEl.getAttribute('data-gate-mode') === 'warn'
                    ? 'warn' : 'downsample';
                var map = (mode === 'warn' ? L_MEM_WARN : L_MEM);
                var fmt = (map[loc] || map.en).note;
                var tail = fmt(Math.round(file.size / 1048576), pre.deviceGB);
                noticeEl = document.createElement('div');
                noticeEl.className = 'device-gate-memory-notice';
                noticeEl.style.cssText = 'margin:12px 0;padding:12px 14px;border-radius:10px;'
                    + 'background:rgba(56,189,248,0.06);border:1px solid rgba(56,189,248,0.2);'
                    + 'color:var(--text-primary,#fff);font-size:13px;line-height:1.5;'
                    + 'display:flex;gap:10px;align-items:flex-start;';
                var icon = document.createElement('span');
                icon.style.cssText = 'font-size:18px;flex-shrink:0;line-height:1';
                icon.textContent = '🪶';
                var msg = document.createElement('span');
                msg.textContent = tail;
                noticeEl.appendChild(icon);
                noticeEl.appendChild(msg);
                if (uploaderEl.parentNode) {
                    uploaderEl.parentNode.insertBefore(noticeEl, uploaderEl.nextSibling);
                }

                _ae(pre.severity === 'blocked' ? 'memory_blocked' : 'memory_marginal',
                    { slug: slug, fileMB: Math.round(file.size / 1048576), deviceGB: pre.deviceGB,
                      predictedMB: pre.predictedMB });
            };
            // An OBSERVER: this watcher only looks at what was picked. Bound as a
            // plain consumer it took the pick's stamp at dispatch, and a tool
            // whose scripts were still on their way was then replayed nothing
            // (the same defect as the limit primitive's, 07.10.2026 — measured on
            // production: the watcher was the first binder on 9 zones).
            if (typeof window.__bindUploaderFile === 'function') {
                window.__bindUploaderFile(uploaderEl, fileHandler, { observer: true });
            } else {
                uploaderEl.addEventListener('tool-uploader:file', function(ev) {
                    var f = ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0]));
                    fileHandler(f);
                });
            }

            // The file is gone from the page — so is the notice about it. (Until
            // 07.10.2026 this listened for `tool-uploader:reset`, an event the
            // zone has never sent: the notice outlived the file it was about.)
            uploaderEl.addEventListener('tool-uploader:clear', clear);
        },

        /**
         * One-shot bootstrap: read `data-gate` on the uploader element, install
         * everything declared. Called from tool-uploader.js once the uploader
         * finishes its own setup. Tools never need to call gate APIs directly.
         */
        autoBootstrap: function(uploaderEl) {
            if (!uploaderEl || uploaderEl.__deviceGateBooted) return;
            uploaderEl.__deviceGateBooted = true;
            var gate = uploaderEl.getAttribute('data-gate') || '';
            var parts = gate.split('+').map(function(s){ return s.trim(); }).filter(Boolean);
            var slug = uploaderEl.getAttribute('data-tool-slug') || null;
            if (parts.indexOf('ffmpeg') !== -1) this.installAdvisory(uploaderEl, { slug: slug });
            if (parts.indexOf('memory') !== -1) this.installMemoryWatcher(uploaderEl, { slug: slug });
        }
    };

    window.DeviceCapabilityGate = _api;

    // Auto-discover existing uploaders that already declared `data-gate` —
    // covers the case where the gate script loads AFTER tool-uploader.js
    // (defer order race). Uploader's own bootstrap also calls autoBootstrap,
    // so this is a belt-and-braces second pass.
    function _scanAndBoot() {
        var els = document.querySelectorAll('[data-gate]');
        for (var i = 0; i < els.length; i++) _api.autoBootstrap(els[i]);
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', _scanAndBoot);
    } else {
        _scanAndBoot();
    }
})();
